import { BreakpointObserver } from "@angular/cdk/layout";
import type { HttpErrorResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormGroupDirective,
  NonNullableFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { AnalyticsService } from "@cariloop/analytics";
import {
  emailPattern,
  SupportedLanguage,
  zipCodeRegexCA,
} from "@cariloop/ui-components";
import { TranslateService } from "@ngx-translate/core";
import { AccountService } from "app/shared/services/account.service";
import {
  DevicePlatformService,
  MixPanelPlatform,
} from "app/shared/services/device-platform.service";
import {
  BackgroundTaskService,
  BackgroundTaskStatus,
} from "app/shared/services/background-task.service";
import type { RegisteredEmail } from "app/shared/types/register.types";
import { EMPTY, type Observable, of, race, timer } from "rxjs";
import {
  catchError,
  filter,
  map,
  startWith,
  switchMap,
  take,
  tap,
} from "rxjs/operators";

type EligibilityCheckMethod = "workEmail" | "personalInformation";

type WorkEmailGroup = FormGroup<{
  workEmail: FormControl<string>;
}>;

type PersonalEmailGroup = FormGroup<PersonalEmailFormGroup>;

interface PersonalEmailFormGroup {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  zipCode: FormControl<string>;
  dateOfBirth: FormControl<string>;
}

type EligibilityCheckFormGroup = FormGroup<{
  method: FormControl<EligibilityCheckMethod>;
  workEmailGroup: WorkEmailGroup;
  personalEmailGroup: PersonalEmailGroup;
}>;

type EligibilityCheckForm = {
  method: EligibilityCheckMethod;
  workEmailGroup: WorkEmailGroup;
  personalEmailGroup: PersonalEmailGroup;
};

const ACTIVE_ACCOUNT_FOUND_ERROR_STATUS = 422;
const ACTIVE_ACCOUNT_FOUND_ERROR_MESSAGE = "Active account records found";
const MULTIPLE_ELIGIBLE_ACCOUNTS_ERROR_STATUS = 422;
const MULTIPLE_ELIGIBLE_ACCOUNTS_ERROR_MESSAGE =
  "Multiple eligibility records found";
const NO_ELIGIBLE_ACCOUNTS_ERROR_STATUS = 404;
const BACKGROUND_TASK_WAIT_TIMEOUT_MS = 10_000;

type State = {
  hasActiveAccountWarning: boolean;
  isUsingSameEmail: boolean;
  hasEligibilityError: boolean;
  hasServerSideError: boolean;
  isSubmitting: boolean;
};

const INITIAL_STATE: State = {
  hasActiveAccountWarning: false,
  isUsingSameEmail: false,
  hasEligibilityError: false,
  hasServerSideError: false,
  isSubmitting: false,
};

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  // We need this to change inner material styles and follow the design.
  encapsulation: ViewEncapsulation.None,
  selector: "app-eligibility-check",
  templateUrl: "./eligibility-check.component.html",
  styleUrls: ["./eligibility-check.component.scss"],
  standalone: false,
})
export class EligibilityCheckComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input() enrollmentCode: string | undefined;
  @Input({ required: true })
  registeredEmail: null | RegisteredEmail;
  @ViewChild(FormGroupDirective) formRef: FormGroupDirective;
  @ViewChildren("zipCodeInput", { read: ElementRef }) zipCodeInputs: QueryList<
    ElementRef<HTMLInputElement>
  >;

  @Output()
  formSubmitted = new EventEmitter<void>();

  today = new Date();
  isDOBTouched = false;
  errorValidationBanner: boolean = false;
  eligibilityCheckForm: EligibilityCheckFormGroup;
  selectedMethod$: Observable<EligibilityCheckMethod>;
  accountName: { firstName: string; lastName: string };
  zipCodeElement: HTMLElement;
  datePickerElement: HTMLElement;
  elementsWithListeners: HTMLElement[] = [];
  personalInfoCheckFailed: boolean = false;
  workEmailCheckFailed: boolean = false;
  platform: MixPanelPlatform;
  isMFAEnforced: boolean = false;
  bgTaskId?: string;

  protected readonly hasSubmitted = signal(false);
  state = signal<State>(INITIAL_STATE);
  readonly #translateService = inject(TranslateService);
  readonly #titleService = inject(Title);

  readonly #breakpointObserver = inject(BreakpointObserver);
  protected readonly SupportedLanguage = SupportedLanguage;
  protected isMobileWidth$ = this.#breakpointObserver
    .observe("(max-width: 576px)")
    .pipe(map((breakpointState) => breakpointState.matches));

  @ViewChildren("activeAccountWarning", { read: ElementRef })
  private activeAccountWarning: QueryList<ElementRef>;
  @ViewChildren("eligibilityWarning", { read: ElementRef })
  private eligibilityWarning: QueryList<ElementRef>;
  @ViewChildren("serverSideError", { read: ElementRef })
  private serverSideError: QueryList<ElementRef>;

  readonly #destroyRef = inject(DestroyRef);
  @Output() MFAEnforcedChange = new EventEmitter<boolean>();

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly accountService: AccountService,
    private readonly analyticsService: AnalyticsService,
    private readonly devicePlatformService: DevicePlatformService,
    private readonly route: ActivatedRoute,
    private readonly backgroundTaskService: BackgroundTaskService,
    private readonly router: Router,
  ) {
    this.#translateService
      .stream("REGISTER.PAGE_TITLE")
      .pipe(
        tap((pageTitle: string) => {
          this.#titleService.setTitle(`${pageTitle} | Cariloop`);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  ngOnInit() {
    const accountResult = this.accountService.getLogged();
    if (accountResult instanceof Error) {
      throw accountResult;
    }

    const { firstName, lastName, id } = accountResult;

    this.accountName = { firstName, lastName };
    this.eligibilityCheckForm = this.formBuilder.group<EligibilityCheckForm>({
      method: "workEmail",
      workEmailGroup: this.formBuilder.group({
        workEmail: [
          this.registeredEmail?.type === "work"
            ? this.registeredEmail.address
            : "",
          [Validators.pattern(emailPattern)],
        ],
      }),
      personalEmailGroup: this.formBuilder.group({
        firstName: [firstName as string],
        lastName: [lastName as string],
        zipCode: ["", this.zipCodePatternValidator()],
        dateOfBirth: [""],
      }),
    });

    this.selectedMethod$ = this.eligibilityCheckForm
      .get("method")
      .valueChanges.pipe(
        startWith(this.eligibilityCheckForm.get("method").value),
      );

    this.platform = this.devicePlatformService.getPlatformForMixpanel();

    const isReturning = this.route.snapshot.routeConfig.path === "complete";
    this.analyticsService.trackEvent("eligibility-check", {
      platform: this.platform,
      source: "auth",
      enrollmentCode: this.enrollmentCode,
      accountId: id,
      ...(isReturning && { userFlow: "update-existing-account" }),
      ...(!isReturning && { userFlow: "create-new-account" }),
    });
  }

  ngAfterViewInit(): void {
    this.zipCodeInputs.changes.subscribe((q) => {
      this.zipCodeElement = q.first?.nativeElement;

      const verificationMethod = this.eligibilityCheckForm.get("method").value;

      if (verificationMethod === "personalInformation" && this.zipCodeElement) {
        this.zipCodeElement.addEventListener("blur", () => {
          const zipCodeControl = (
            this.eligibilityCheckForm.get(
              "personalEmailGroup",
            ) as FormGroup<PersonalEmailFormGroup>
          ).controls["zipCode"];

          zipCodeControl.updateValueAndValidity();
        });
        this.elementsWithListeners.push(this.zipCodeElement);
      }

      this.datePickerElement = document
        .querySelector(".date-picker")
        ?.querySelector("input");
      if (this.datePickerElement) {
        this.datePickerElement.addEventListener("blur", () => {
          this.isDOBTouched = true;
        });
        this.elementsWithListeners.push(this.datePickerElement);
      }
    });

    this.activeAccountWarning.changes
      .pipe(
        tap(() => {
          if (this.activeAccountWarning.length === 1) {
            this.activeAccountWarning.first.nativeElement.focus();
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();

    this.eligibilityWarning.changes
      .pipe(
        tap(() => {
          if (this.eligibilityWarning.length === 1) {
            this.eligibilityWarning.first.nativeElement.focus();
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();

    this.serverSideError.changes
      .pipe(
        tap(() => {
          if (this.serverSideError.length === 1) {
            this.serverSideError.first.nativeElement.focus();
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.elementsWithListeners.forEach((element) => {
      element.removeAllListeners("blur");
    });

    this.#titleService.setTitle("Cariloop");
  }

  handleSubmit() {
    const accountResult = this.accountService.getLogged();
    if (accountResult instanceof Error) {
      this.router.navigate(["/"], { queryParamsHandling: "preserve" });
      return;
    }

    this.hasSubmitted.set(true);

    const selectedMethod = this.eligibilityCheckForm.get("method").value;

    if (selectedMethod === "workEmail") {
      this.eligibilityCheckForm
        .get("workEmailGroup")
        .get("workEmail")
        .setValidators([Validators.pattern(emailPattern), Validators.required]);
    }
    if (selectedMethod === "personalInformation") {
      this.eligibilityCheckForm
        .get("personalEmailGroup")
        .get("firstName")
        .setValidators(Validators.required);
      this.eligibilityCheckForm
        .get("personalEmailGroup")
        .get("lastName")
        .setValidators(Validators.required);
      this.eligibilityCheckForm
        .get("personalEmailGroup")
        .get("zipCode")
        .setValidators([this.zipCodePatternValidator(), Validators.required]);
    }

    const { id } = accountResult;

    this.state.set(INITIAL_STATE);

    this.eligibilityCheckForm.removeControl(
      selectedMethod === "workEmail" ? "personalEmailGroup" : "workEmailGroup",
    );

    // This is needed because `c-input-text`s only shows their inner `mat-error` if the input is `touched`
    this.eligibilityCheckForm.markAllAsTouched();
    this.eligibilityCheckForm.updateValueAndValidity();

    if (!this.eligibilityCheckForm.valid) {
      this.resetFormControls();
      return;
    }

    this.state.update((currentState) => ({
      ...currentState,
      isSubmitting: true,
    }));

    if (selectedMethod === "personalInformation") {
      this.eligibilityCheckForm.get("personalEmailGroup").disable();
    } else {
      const workEmail = this.eligibilityCheckForm
        .get("workEmailGroup")
        .get("workEmail")
        .value.trim()
        .toLowerCase();
      if (
        accountResult.email &&
        accountResult.email.toLowerCase() === workEmail
      ) {
        this.resetFormControls();
        this.resetForm();
        this.eligibilityCheckForm.get("workEmailGroup").enable();
        this.state.update((currentState) => ({
          ...currentState,
          isUsingSameEmail: true,
          isSubmitting: false,
        }));
        return;
      }
    }

    const eligibilityCheckData =
      selectedMethod === "workEmail"
        ? {
            kind: selectedMethod,
            workEmail: this.eligibilityCheckForm
              .get("workEmailGroup")
              .get("workEmail")
              .value.trim(),
          }
        : {
            kind: selectedMethod,
            firstName: this.eligibilityCheckForm
              .get("personalEmailGroup.firstName")
              .value.trim(),
            lastName: this.eligibilityCheckForm
              .get("personalEmailGroup.lastName")
              .value.trim(),
            zipCode: this.normalizeZipCode(
              this.eligibilityCheckForm.get("personalEmailGroup.zipCode").value,
            ),
            birthDate: new Date(
              this.eligibilityCheckForm.get("personalEmailGroup.dateOfBirth")
                .value,
            ).toLocaleDateString("en-US"),
          };
    this.accountService
      .eligibilityCheck(eligibilityCheckData)
      .pipe(
        tap((data) => {
          this.isMFAEnforced = data.isMFAEnforced;
          this.bgTaskId = data.taskId;
          this.MFAEnforcedChange.emit(this.isMFAEnforced);
          const isReturning =
            this.route.snapshot.routeConfig.path === "complete";
          this.analyticsService.trackEvent("eligibility-check-complete", {
            platform: this.platform,
            source: "auth",
            enrollmentCode: this.enrollmentCode,
            accountId: id,
            ...(isReturning && { userFlow: "update-existing-account" }),
            ...(!isReturning && { userFlow: "create-new-account" }),
          });
          this.analyticsService.trackEvent("eligibility-check-method", {
            platform: this.platform,
            source: "auth",
            accountId: id,
            enrollmentCode: this.enrollmentCode,
            userFlow: isReturning
              ? "update-existing-account"
              : "create-new-account",
            method: selectedMethod,
          });
        }),
        switchMap(({ taskId }) =>
          taskId ? this.waitForBackgroundTaskCompletion(taskId) : of(null),
        ),
        takeUntilDestroyed(this.#destroyRef),
        catchError(
          ({
            status,
            error: {
              error: { message },
            },
          }: HttpErrorResponse) => {
            this.resetFormControls();
            if (selectedMethod === "personalInformation") {
              this.eligibilityCheckForm.get("personalEmailGroup").enable();
            } else {
              this.resetForm();
            }

            if (
              status === ACTIVE_ACCOUNT_FOUND_ERROR_STATUS &&
              message === ACTIVE_ACCOUNT_FOUND_ERROR_MESSAGE
            ) {
              this.state.update((currentState) => ({
                ...currentState,
                isSubmitting: false,
                hasEligibilityError: false,
                isUsingSameEmail: false,
                hasActiveAccountWarning: true,
              }));

              if (this.activeAccountWarning.length === 1) {
                this.activeAccountWarning.first.nativeElement.focus();
              }

              return EMPTY;
            }

            if (
              (status === MULTIPLE_ELIGIBLE_ACCOUNTS_ERROR_STATUS &&
                message === MULTIPLE_ELIGIBLE_ACCOUNTS_ERROR_MESSAGE) ||
              status === NO_ELIGIBLE_ACCOUNTS_ERROR_STATUS
            ) {
              this.state.update((currentState) => ({
                ...currentState,
                isSubmitting: false,
                hasEligibilityError: true,
                isUsingSameEmail: false,
                hasActiveAccountWarning: false,
              }));

              if (selectedMethod === "personalInformation") {
                this.personalInfoCheckFailed = true;
              }
              if (selectedMethod === "workEmail") {
                this.workEmailCheckFailed = true;
              }

              if (this.eligibilityWarning.length === 1) {
                this.eligibilityWarning.first.nativeElement.focus();
              }
              return EMPTY;
            }

            this.state.update((currentState) => ({
              ...currentState,
              isSubmitting: false,
              hasServerSideError: true,
            }));

            if (this.serverSideError.length === 1) {
              this.serverSideError.first.nativeElement.focus();
            }
            return EMPTY;
          },
        ),
      )
      .subscribe({
        next: () => {
          this.state.update((currentState) => ({
            ...currentState,
            isSubmitting: false,
          }));

          if (selectedMethod === "personalInformation") {
            this.eligibilityCheckForm.get("personalEmailGroup").enable();
          }

          this.formSubmitted.emit();
        },
      });
  }

  private waitForBackgroundTaskCompletion(
    taskId: string,
  ): Observable<BackgroundTaskStatus | null> {
    return race(
      this.backgroundTaskService.pollStatusUntilComplete(taskId).pipe(
        filter(({ status }) => status === "completed"),
        take(1),
      ),
      timer(BACKGROUND_TASK_WAIT_TIMEOUT_MS).pipe(map(() => null)),
    ).pipe(take(1));
  }

  private zipCodePatternValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let value = control.value;

      if (!value) {
        return null;
      }

      value = value.trim();

      const usZipRegex = /^\d{5}(?:[-\s]\d{4})?$/;

      const isValid = usZipRegex.test(value) || zipCodeRegexCA.test(value);

      return !isValid ? { pattern: true } : null;
    };
  }

  private resetForm() {
    this.isDOBTouched = false;
    this.formRef.resetForm();
  }

  resetFormControls() {
    this.eligibilityCheckForm.addControl(
      "personalEmailGroup",
      this.formBuilder.group({
        firstName: [this.accountName.firstName as string, Validators.required],
        lastName: [this.accountName.lastName as string, Validators.required],
        zipCode: ["", [Validators.required, this.zipCodePatternValidator()]],
        dateOfBirth: [""],
      }),
    );
    this.eligibilityCheckForm.addControl(
      "workEmailGroup",
      this.formBuilder.group({
        workEmail: [
          this.registeredEmail?.type === "work"
            ? this.registeredEmail.address
            : "",
          [Validators.pattern(emailPattern), Validators.required],
        ],
      }),
    );
  }

  selectEligibilityCheckMethod(
    event: MouseEvent | KeyboardEvent,
    selectedMethod: EligibilityCheckMethod,
  ) {
    this.eligibilityCheckForm.get("method").setValue(selectedMethod);
    (event.target as HTMLElement).blur();
  }

  private normalizeZipCode(zipCode: string): string {
    zipCode = zipCode.trim();
    const isCanadianZipCode = zipCodeRegexCA.test(zipCode);
    if (isCanadianZipCode) {
      return zipCode.replace(/\s/g, "").toUpperCase();
    }
    return zipCode;
  }

  protected handleLoginClick() {
    this.accountService
      .logout()
      .pipe(
        tap(() => {
          this.router.navigate(["/login"]);
        }),
      )
      .subscribe();
  }

  protected handleResetPasswordClick() {
    this.accountService
      .logout()
      .pipe(
        tap(() => {
          this.router.navigate(["/forgot-password"]);
        }),
      )
      .subscribe();
  }
}
